import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';

/**
 * Console interattiva per i tool MCP-style esposti dal backend
 * (/api/admin/mcp/*). Mostra l'elenco, il JSON schema input, ed esegue
 * la chiamata mostrando il risultato (o l'errore) come JSON formattato.
 *
 * Coerente per nome/firma con il server MCP standalone
 * (mcp-servers/diagnostics/), cosi' chi e' abituato a usarlo da Claude
 * Code ritrova la stessa shape qui.
 */
interface McpToolDescriptor {
    name: string;
    description: string;
    inputSchema: {
        type?: string;
        required?: string[];
        properties?: Record<string, {
            type?: string;
            enum?: string[];
            default?: unknown;
            minimum?: number;
            maximum?: number;
        }>;
    };
}

interface McpCallResponse {
    tool: string;
    isError: boolean;
    result?: unknown;
    error?: string;
    durationMs: number;
}

const McpConsole: React.FC = () => {
    const { showToast } = useToast();
    const [tools, setTools] = useState<McpToolDescriptor[]>([]);
    const [selected, setSelected] = useState<string>('');
    const [argsJson, setArgsJson] = useState<string>('{}');
    const [response, setResponse] = useState<McpCallResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [calling, setCalling] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const list = await apiService.mcpListTools<McpToolDescriptor>();
                setTools(list);
                if (list.length > 0) {
                    setSelected(list[0].name);
                    setArgsJson(makeDefaultInput(list[0]));
                }
            } catch {
                showToast('Errore', 'Impossibile caricare la lista tool MCP', 'danger');
            } finally {
                setLoading(false);
            }
        })();
    }, [showToast]);

    const selectedTool = useMemo(
        () => tools.find(t => t.name === selected) ?? null, [tools, selected]);

    const handleSelect = (name: string) => {
        setSelected(name);
        const t = tools.find(x => x.name === name);
        if (t) setArgsJson(makeDefaultInput(t));
        setResponse(null);
    };

    const handleCall = async () => {
        if (!selected) return;
        let input: unknown = null;
        if (argsJson.trim()) {
            try { input = JSON.parse(argsJson); }
            catch (e: any) {
                showToast('JSON non valido', e.message ?? 'parse error', 'warning');
                return;
            }
        }
        setCalling(true);
        setResponse(null);
        try {
            const res = await apiService.mcpCallTool<McpCallResponse>(selected, input);
            setResponse(res);
        } catch (e: any) {
            setResponse({
                tool: selected, isError: true,
                error: e?.message ?? 'Errore HTTP', durationMs: 0,
            });
        } finally {
            setCalling(false);
        }
    };

    return (
        <div className="card shadow-sm h-100">
            <div className="card-header bg-body-tertiary d-flex align-items-center gap-2">
                <i className="bi bi-terminal" />
                <strong>Console MCP</strong>
                <span className="badge bg-secondary ms-1">{tools.length} tool</span>
                <span className="ms-auto small text-body-secondary">
                    Endpoint: <code>/api/admin/mcp/*</code>
                </span>
            </div>
            <div className="card-body d-flex flex-column gap-2" style={{ minHeight: 0 }}>
                {loading && <div className="text-body-secondary small">Caricamento tool…</div>}
                {!loading && tools.length === 0 && (
                    <div className="text-body-secondary small">
                        Nessun tool MCP registrato lato server.
                    </div>
                )}
                {!loading && tools.length > 0 && (
                    <>
                        <label className="form-label small mb-0">Tool</label>
                        <select className="form-select form-select-sm"
                            value={selected} onChange={e => handleSelect(e.target.value)}>
                            {tools.map(t => (
                                <option key={t.name} value={t.name}>{t.name}</option>
                            ))}
                        </select>
                        {selectedTool && (
                            <div className="small text-body-secondary">{selectedTool.description}</div>
                        )}

                        <label className="form-label small mb-0 mt-1">Input (JSON)</label>
                        <textarea className="form-control font-monospace"
                            rows={6} spellCheck={false}
                            style={{ fontSize: '0.8125rem' }}
                            value={argsJson} onChange={e => setArgsJson(e.target.value)} />

                        {selectedTool && (
                            <SchemaHints schema={selectedTool.inputSchema} />
                        )}

                        <button className="btn btn-primary btn-sm align-self-start"
                            onClick={handleCall} disabled={calling}>
                            {calling ? (
                                <><span className="spinner-border spinner-border-sm me-1" />Esecuzione…</>
                            ) : (
                                <><i className="bi bi-play-fill me-1" />Esegui</>
                            )}
                        </button>

                        {response && (
                            <div className="mt-2 flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
                                <div className="d-flex gap-2 align-items-center mb-1">
                                    {response.isError ? (
                                        <span className="badge bg-danger">errore</span>
                                    ) : (
                                        <span className="badge bg-success">ok</span>
                                    )}
                                    <span className="small text-body-secondary">
                                        {response.durationMs} ms
                                    </span>
                                </div>
                                <pre className="bg-body-tertiary border rounded p-2 small font-monospace mb-0"
                                    style={{ flex: 1, overflow: 'auto', maxHeight: 320, fontSize: '0.75rem' }}>
                                    {response.isError
                                        ? response.error
                                        : JSON.stringify(response.result, null, 2)}
                                </pre>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const SchemaHints: React.FC<{ schema: McpToolDescriptor['inputSchema'] }> = ({ schema }) => {
    const required = schema.required ?? [];
    const props = Object.entries(schema.properties ?? {});
    if (props.length === 0) {
        return <div className="small text-body-secondary">Nessun parametro.</div>;
    }
    return (
        <div className="small">
            {props.map(([name, def]) => (
                <div key={name} className="d-flex gap-1 text-body-secondary">
                    <code className={required.includes(name) ? 'text-danger' : ''}>{name}</code>
                    <span className="text-muted">·</span>
                    <span>{def.type}{def.enum ? ` ∈ {${def.enum.join('|')}}` : ''}</span>
                    {def.default !== undefined && <span className="ms-auto">default: <code>{String(def.default)}</code></span>}
                </div>
            ))}
        </div>
    );
};

/**
 * Pre-popola il textarea Input con un JSON che includa SOLO i required.
 * Gli optional restano nascosti per non spaventare con un blob enorme;
 * l'utente li aggiunge a mano se servono.
 */
function makeDefaultInput(t: McpToolDescriptor): string {
    const required = t.inputSchema.required ?? [];
    const props = t.inputSchema.properties ?? {};
    if (required.length === 0) return '{}';
    const obj: Record<string, unknown> = {};
    for (const k of required) {
        const def = props[k];
        if (def?.type === 'integer' || def?.type === 'number') obj[k] = 0;
        else if (def?.enum && def.enum.length > 0) obj[k] = def.enum[0];
        else obj[k] = '';
    }
    return JSON.stringify(obj, null, 2);
}

export default McpConsole;
